import { apiRequest } from './client'
import {
  demoMyPricesAssignProductType,
  demoMyPricesCategorySuggestions,
  demoMyPricesConfirmSuggestion,
  demoMyPricesFilterOptions,
  demoMyPricesItemsPage,
  demoMyPricesNameSuggestions,
  demoMyPricesSummary,
  demoMyPricesUpdateFields,
  demoPrivateSuppliers,
} from '../dev/buyerDemoData'
import { demoUiDelay, isDemoUiEnabled, withDemoUi } from '../dev/demoMode'
import type { MatchCandidateDto } from './catalogImportTypes'
import type { CatalogItemFieldsUpdate } from './supplierCatalog'
import type {
  CatalogItemSortDirection,
  CatalogItemSortKey,
  CatalogItemsPageDto,
  CatalogSummaryDto,
  SupplierCatalogFilterOptionsDto,
  SupplierCatalogItemDto,
} from './supplierCatalog'

const API_PREFIX = '/buyer/private-catalog'

/** Подсказка названия из общей базы: только название и категория, без цены и поставщика. */
export type NameSuggestionDto = {
  name: string
  productTypeName: string | null
  own: boolean
}

type Raw = Record<string, unknown>

const normalizeSummary = (json: Raw): CatalogSummaryDto => ({
  totalItems: Number(json.total_items ?? json.totalItems ?? 0),
  matchAuto: Number(json.match_auto ?? json.matchAuto ?? 0),
  matchSuggested: Number(json.match_suggested ?? json.matchSuggested ?? 0),
  matchManual: Number(json.match_manual ?? json.matchManual ?? 0),
  matchUnmatched: Number(json.match_unmatched ?? json.matchUnmatched ?? 0),
  needsAttention: Number(json.needs_attention ?? json.needsAttention ?? 0),
  staleItems: Number(json.stale_items ?? json.staleItems ?? 0),
  hiddenFromSearch: Number(json.hidden_from_search ?? json.hiddenFromSearch ?? 0),
})

const normalizeItem = (json: Raw): SupplierCatalogItemDto => ({
  id: Number(json.id ?? 0),
  organizationId: Number(json.organizationId ?? json.organization_id ?? 0),
  supplierName: String(json.supplierName ?? json.supplier_name ?? ''),
  matchStatus: String(json.matchStatus ?? json.match_status ?? ''),
  externalSku: (json.externalSku as string | null) ?? (json.external_sku as string | null) ?? null,
  brand: (json.brand as string | null) ?? null,
  country: (json.country as string | null) ?? null,
  unit: (json.unit as string | null) ?? null,
  price: json.price != null ? Number(json.price) : null,
  minShipmentQty:
    json.minShipmentQty != null
      ? Number(json.minShipmentQty)
      : json.min_shipment_qty != null
        ? Number(json.min_shipment_qty)
        : null,
  packageSize: (json.packageSize as string | null) ?? (json.package_size as string | null) ?? null,
  productTypeId:
    json.productTypeId != null
      ? Number(json.productTypeId)
      : json.product_type_id != null
        ? Number(json.product_type_id)
        : null,
  productTypeCode: (json.productTypeCode as string | null) ?? (json.product_type_code as string | null) ?? null,
  productTypeName: (json.productTypeName as string | null) ?? (json.product_type_name as string | null) ?? null,
  productTypeActive:
    json.productTypeActive != null
      ? Boolean(json.productTypeActive)
      : json.product_type_active != null
        ? Boolean(json.product_type_active)
        : null,
  matchConfidence:
    json.matchConfidence != null
      ? Number(json.matchConfidence)
      : json.match_confidence != null
        ? Number(json.match_confidence)
        : null,
  operatorComment: (json.operatorComment as string | null) ?? (json.operator_comment as string | null) ?? null,
  updatedAt: (json.updatedAt as string | null) ?? (json.updated_at as string | null) ?? null,
  freshnessStatus: (() => {
    const raw = (json.freshnessStatus as string | null) ?? (json.freshness_status as string | null) ?? null
    if (raw === 'fresh' || raw === 'stale_7' || raw === 'stale_14' || raw === 'stale_30') {
      return raw
    }
    return null
  })(),
  daysSinceUpdate:
    json.daysSinceUpdate != null
      ? Number(json.daysSinceUpdate)
      : json.days_since_update != null
        ? Number(json.days_since_update)
        : null,
  buyerSearchVisible:
    json.buyerSearchVisible != null
      ? Boolean(json.buyerSearchVisible)
      : json.buyer_search_visible != null
        ? Boolean(json.buyer_search_visible)
        : null,
  privateSupplierId:
    json.privateSupplierId != null
      ? Number(json.privateSupplierId)
      : json.private_supplier_id != null
        ? Number(json.private_supplier_id)
        : null,
  privateSupplierName:
    (json.privateSupplierName as string | null) ??
    (json.private_supplier_name as string | null) ??
    null,
  previousPrice:
    json.previousPrice != null
      ? Number(json.previousPrice)
      : json.previous_price != null
        ? Number(json.previous_price)
        : null,
  priceDeltaPct:
    json.priceDeltaPct != null
      ? Number(json.priceDeltaPct)
      : json.price_delta_pct != null
        ? Number(json.price_delta_pct)
        : null,
  previousObservedAt:
    (json.previousObservedAt as string | null) ??
    (json.previous_observed_at as string | null) ??
    null,
  catalogListIds: ((json.catalogListIds as unknown[] | undefined) ?? (json.catalog_list_ids as unknown[] | undefined) ?? [])
    .map(Number)
    .filter(Number.isFinite),
})

const normalizePage = (json: Raw): CatalogItemsPageDto => ({
  items: ((json.items as Raw[] | undefined) ?? []).map(normalizeItem),
  page: Number(json.page ?? 0),
  size: Number(json.size ?? 0),
  totalElements: Number(json.totalElements ?? json.total_elements ?? 0),
  totalPages: Number(json.totalPages ?? json.total_pages ?? 0),
})

export const getRestaurantMyPricesSummary = async (
  organizationId: number,
): Promise<CatalogSummaryDto> =>
  withDemoUi(demoMyPricesSummary(), async () => {
  const result = await apiRequest<Raw>(`${API_PREFIX}/summary?organizationId=${organizationId}`)
  return normalizeSummary(result)
})

export const listRestaurantMyPricesItems = async (options: {
  organizationId: number
  page?: number
  size?: number
  matchStatus?: string
  productTypeId?: number | null
  /** Все id выбранной ветки (родитель + потомки) — для demo и будущего API. */
  productTypeIds?: number[]
  unassignedProductType?: boolean
  /** «Предложено» или «без категории» — счётчик «требуют внимания» в сводке. */
  needsAttention?: boolean
  search?: string
  privateSupplierId?: number | null
  catalogListId?: number | null
  sortBy?: CatalogItemSortKey
  sortDirection?: CatalogItemSortDirection
}): Promise<CatalogItemsPageDto> =>
  withDemoUi(
    demoMyPricesItemsPage({
      needsAttention: options.needsAttention,
      page: options.page,
      size: options.size,
      matchStatus: options.matchStatus,
      productTypeId: options.productTypeId,
      productTypeIds: options.productTypeIds,
      unassignedProductType: options.unassignedProductType,
      search: options.search,
      privateSupplierId: options.privateSupplierId,
      catalogListId: options.catalogListId,
      sortBy: options.sortBy,
      sortDirection: options.sortDirection,
    }),
    async () => {
  const params = new URLSearchParams({
    organizationId: String(options.organizationId),
    page: String(options.page ?? 0),
    size: String(options.size ?? 50),
  })
  if (options.matchStatus) {
    params.set('matchStatus', options.matchStatus)
  }
  if (options.productTypeId != null) {
    params.set('productTypeId', String(options.productTypeId))
  }
  if (options.unassignedProductType) {
    params.set('unassignedProductType', 'true')
  }
  if (options.needsAttention) {
    params.set('needs_attention', 'true')
  }
  if (options.search?.trim()) {
    params.set('search', options.search.trim())
  }
  if (options.privateSupplierId != null) {
    params.set('private_supplier_id', String(options.privateSupplierId))
  }
  if (options.catalogListId != null) {
    params.set('catalog_list_id', String(options.catalogListId))
  }
  if (options.sortBy) {
    params.set('sortBy', options.sortBy)
    params.set('sortDirection', options.sortDirection ?? 'asc')
  }
  const result = await apiRequest<Raw>(`${API_PREFIX}/items?${params.toString()}`)
  return normalizePage(result)
},
)

export const listRestaurantMyPricesFilterOptions = async (
  organizationId: number,
): Promise<SupplierCatalogFilterOptionsDto> =>
  withDemoUi(demoMyPricesFilterOptions(), async () => {
  const response = await apiRequest<Raw>(`${API_PREFIX}/filter-options?organizationId=${organizationId}`)
  return {
    brands: (response.brands as string[] | undefined) ?? [],
    countries: (response.countries as string[] | undefined) ?? [],
  }
})

export const updateRestaurantMyPricesItemFields = async (
  itemId: number,
  update: CatalogItemFieldsUpdate & {
    /** Перевесить позицию на другую карточку поставщика своей организации. */
    privateSupplierId?: number
  },
): Promise<SupplierCatalogItemDto> => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    return demoMyPricesUpdateFields(itemId, {
      supplierName: update.name ?? undefined,
      price: update.price ?? undefined,
      unit: update.unit ?? undefined,
      brand: update.brand,
      country: update.country,
      packageSize: update.packageSize,
      minShipmentQty: update.minShipmentQty ?? undefined,
      privateSupplierId: update.privateSupplierId,
    })
  }
  const body: Record<string, unknown> = {}
  if (update.name !== undefined) body.name = update.name
  if (update.brand !== undefined) body.brand = update.brand
  if (update.price !== undefined) body.price = update.price
  if (update.country !== undefined) body.country = update.country
  if (update.unit !== undefined) body.unit = update.unit
  if (update.minShipmentQty !== undefined) body.min_shipment_qty = update.minShipmentQty
  if (update.packageSize !== undefined) body.package_size = update.packageSize
  if (update.comment !== undefined) body.comment = update.comment
  if (update.privateSupplierId !== undefined) body.private_supplier_id = update.privateSupplierId

  const result = await apiRequest<Raw>(`${API_PREFIX}/items/${itemId}/fields`, {
    method: 'PATCH',
    body,
  })
  return normalizeItem(result)
}

export const assignRestaurantMyPricesProductType = async (
  itemId: number,
  productTypeId: number,
): Promise<SupplierCatalogItemDto> => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    return demoMyPricesAssignProductType(itemId, productTypeId)
  }
  const result = await apiRequest<Raw>(`${API_PREFIX}/items/${itemId}/product-type`, {
    method: 'PATCH',
    body: { product_type_id: productTypeId },
  })
  return normalizeItem(result)
}

export const confirmRestaurantMyPricesSuggestion = async (
  itemId: number,
): Promise<SupplierCatalogItemDto> => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    return demoMyPricesConfirmSuggestion(itemId)
  }
  const result = await apiRequest<Raw>(`${API_PREFIX}/items/${itemId}/confirm-suggestion`, {
    method: 'POST',
  })
  return normalizeItem(result)
}

/** Кандидаты категории для «Возможно»: матчер без платного LLM, порядок — по уверенности. */
export const getMyPricesCategorySuggestions = async (itemId: number): Promise<MatchCandidateDto[]> => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    return demoMyPricesCategorySuggestions(itemId)
  }
  const result = await apiRequest<Raw>(`${API_PREFIX}/items/${itemId}/category-suggestions`)
  return ((result.candidates as Raw[] | undefined) ?? []).map((raw) => ({
    productTypeId: Number(raw.product_type_id ?? raw.productTypeId ?? 0),
    code: String(raw.code ?? raw.product_type_code ?? raw.productTypeCode ?? ''),
    name: String(raw.name ?? raw.product_type_name ?? raw.productTypeName ?? ''),
    score: Number(raw.score ?? 0),
  }))
}

export const getMyPricesNameSuggestions = async (
  organizationId: number,
  query: string,
): Promise<NameSuggestionDto[]> => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    return demoMyPricesNameSuggestions(query)
  }
  const params = new URLSearchParams({ organizationId: String(organizationId), q: query })
  const result = await apiRequest<Raw[]>(`${API_PREFIX}/name-suggestions?${params.toString()}`)
  return result.map((raw) => ({
    name: String(raw.name ?? ''),
    productTypeName: (raw.product_type_name as string | null | undefined) ?? null,
    own: Boolean(raw.own),
  }))
}

export const deleteRestaurantMyPricesItem = async (itemId: number): Promise<number> => {
  const result = await apiRequest<Raw>(`${API_PREFIX}/items/${itemId}`, { method: 'DELETE' })
  return Number(result.deleted ?? 0)
}

export const bulkDeleteRestaurantMyPricesItems = async (options: {
  organizationId: number
  itemIds?: number[]
  deleteAll?: boolean
}): Promise<number> => {
  const result = await apiRequest<Raw>(`${API_PREFIX}/items/bulk-delete`, {
    method: 'POST',
    body: {
      organization_id: options.organizationId,
      ...(options.itemIds?.length ? { item_ids: options.itemIds } : {}),
      delete_all: options.deleteAll ?? false,
    },
  })
  return Number(result.deleted ?? 0)
}

export type RestaurantPrivateSupplierDto = {
  id: number
  legalName: string
  ogrn: string
  /** Legacy: у карточек, заведённых до перехода на ОГРН. */
  inn: string
  displayLabel: string | null
  linkedSupplierOrganizationId: number | null
  isRegisteredSupplier: boolean
  /** Минимальная сумма заказа. null — не заполнено либо отмечено как неизвестная. */
  minOrderAmount: number | null
  /** Порог спрашивали, поставщик его не назвал. Отличается от «ещё не заполняли». */
  minOrderAmountUnknown: boolean
  deliverySameDay: boolean
  deliveryNextDay: boolean
  deliveryWeekends: boolean
  /** Контакты менеджера поставщика. На сервере хранятся зашифрованными. */
  managerName: string | null
  managerPhone: string | null
  managerEmail: string | null
}

const normalizePrivateSupplier = (json: Raw): RestaurantPrivateSupplierDto => ({
  id: Number(json.id ?? 0),
  legalName: String(json.legal_name ?? json.legalName ?? ''),
  ogrn: String(json.ogrn ?? ''),
  inn: String(json.inn ?? ''),
  displayLabel: (json.display_label as string | null) ?? (json.displayLabel as string | null) ?? null,
  linkedSupplierOrganizationId:
    json.linked_supplier_organization_id != null
      ? Number(json.linked_supplier_organization_id)
      : json.linkedSupplierOrganizationId != null
        ? Number(json.linkedSupplierOrganizationId)
        : null,
  isRegisteredSupplier:
    json.is_registered_supplier != null
      ? Boolean(json.is_registered_supplier)
      : Boolean(json.isRegisteredSupplier),
  minOrderAmount:
    json.min_order_amount != null
      ? Number(json.min_order_amount)
      : json.minOrderAmount != null
        ? Number(json.minOrderAmount)
        : null,
  minOrderAmountUnknown:
    json.min_order_amount_unknown != null
      ? Boolean(json.min_order_amount_unknown)
      : Boolean(json.minOrderAmountUnknown),
  deliverySameDay:
    json.delivery_same_day != null
      ? Boolean(json.delivery_same_day)
      : Boolean(json.deliverySameDay),
  deliveryNextDay:
    json.delivery_next_day != null
      ? Boolean(json.delivery_next_day)
      : Boolean(json.deliveryNextDay),
  deliveryWeekends:
    json.delivery_weekends != null
      ? Boolean(json.delivery_weekends)
      : Boolean(json.deliveryWeekends),
  managerName: (json.manager_name as string | null) ?? (json.managerName as string | null) ?? null,
  managerPhone:
    (json.manager_phone as string | null) ?? (json.managerPhone as string | null) ?? null,
  managerEmail:
    (json.manager_email as string | null) ?? (json.managerEmail as string | null) ?? null,
})

export const listRestaurantPrivateSuppliers = async (
  organizationId: number,
  venueId?: number | null,
): Promise<RestaurantPrivateSupplierDto[]> => {
  const params = new URLSearchParams({ organizationId: String(organizationId) })
  if (venueId != null) {
    params.set('venue_id', String(venueId))
  }
  return withDemoUi(demoPrivateSuppliers(), async () => {
    const result = await apiRequest<Raw[]>(`${API_PREFIX}/suppliers?${params.toString()}`)
    return (result ?? []).map((entry) => normalizePrivateSupplier(entry as Raw))
  })
}

export const updateRestaurantPrivateSupplier = async (
  privateSupplierId: number,
  patch: {
    legalName?: string | null
    /** undefined — не менять; пустая строка — стереть; иначе номер. */
    ogrn?: string
    minOrderAmount?: number | null
    minOrderAmountUnknown: boolean
    inn?: string | null
    managerName?: string | null
    managerPhone?: string | null
    managerEmail?: string | null
  },
): Promise<RestaurantPrivateSupplierDto> => {
  const demoResult: RestaurantPrivateSupplierDto = {
    ...(demoPrivateSuppliers().find((entry) => entry.id === privateSupplierId) ??
      demoPrivateSuppliers()[0]!),
    id: privateSupplierId,
    legalName:
      patch.legalName?.trim() ||
      (demoPrivateSuppliers().find((entry) => entry.id === privateSupplierId)?.legalName ?? ''),
    minOrderAmount: patch.minOrderAmountUnknown ? null : (patch.minOrderAmount ?? null),
    minOrderAmountUnknown: patch.minOrderAmountUnknown,
    inn: patch.inn ?? '',
    ...(patch.ogrn !== undefined ? { ogrn: patch.ogrn } : {}),
    managerName: patch.managerName ?? null,
    managerPhone: patch.managerPhone ?? null,
    managerEmail: patch.managerEmail ?? null,
  }
  return withDemoUi(demoResult, async () => {
    const result = await apiRequest<Raw>(`${API_PREFIX}/suppliers/${privateSupplierId}`, {
      method: 'PATCH',
      body: {
        legal_name: patch.legalName ?? null,
        ...(patch.ogrn !== undefined ? { ogrn: patch.ogrn } : {}),
        // Сумму и отметку «неизвестна» сервер сводит сам: отметка стирает сумму.
        min_order_amount: patch.minOrderAmountUnknown ? null : (patch.minOrderAmount ?? null),
        min_order_amount_unknown: patch.minOrderAmountUnknown,
        inn: patch.inn ?? null,
        manager_name: patch.managerName ?? null,
        manager_phone: patch.managerPhone ?? null,
        manager_email: patch.managerEmail ?? null,
      },
    })
    return normalizePrivateSupplier(result as Raw)
  })
}

type CatalogDuplicateMatchReason = 'EXTERNAL_SKU' | 'IMPORT_SOURCE_NAME' | 'NAME_SIMILAR'

export type MyPricesDuplicateGroupDto = {
  matchReason: CatalogDuplicateMatchReason
  matchKey: string
  items: SupplierCatalogItemDto[]
}

export type MyPricesDuplicateGroupsResponse = {
  organizationId: number
  groups: MyPricesDuplicateGroupDto[]
}

export type MergeMyPricesDuplicatesResult = {
  keptItem: SupplierCatalogItemDto
  mergedCount: number
}

const normalizeDuplicateReason = (raw: string | null): CatalogDuplicateMatchReason => {
  if (raw === 'EXTERNAL_SKU' || raw === 'IMPORT_SOURCE_NAME' || raw === 'NAME_SIMILAR') {
    return raw
  }
  return 'NAME_SIMILAR'
}

const readString = (obj: Raw, camel: string, snake: string): string | null => {
  const value = obj[camel] ?? obj[snake]
  return value == null ? null : String(value)
}

const readInt = (obj: Raw, camel: string, snake: string): number =>
  Number(obj[camel] ?? obj[snake] ?? 0)

export const listMyPricesDuplicates = async (
  organizationId: number,
): Promise<MyPricesDuplicateGroupsResponse> => {
  const result = await apiRequest<Raw>(`${API_PREFIX}/duplicates?organizationId=${organizationId}`)
  const groups = ((result.groups as Raw[] | undefined) ?? []).map((group) => ({
    matchReason: normalizeDuplicateReason(readString(group, 'matchReason', 'match_reason')),
    matchKey: readString(group, 'matchKey', 'match_key') ?? '',
    items: ((group.items as Raw[] | undefined) ?? []).map(normalizeItem),
  }))
  return {
    organizationId: readInt(result, 'organizationId', 'organization_id'),
    groups,
  }
}

export type DismissMyPricesDuplicatesResult = {
  organizationId: number
  dismissedPairs: number
  alreadyDismissedPairs: number
}

/** «Это не дубликаты» в «Моих прайсах» — решение организации (в пределах одного внешнего поставщика). */
export const dismissMyPricesDuplicates = async (options: {
  organizationId: number
  itemIds: number[]
}): Promise<DismissMyPricesDuplicatesResult> => {
  const result = await apiRequest<Raw>(`${API_PREFIX}/duplicates/dismiss`, {
    method: 'POST',
    body: {
      organization_id: options.organizationId,
      item_ids: options.itemIds,
    },
  })
  return {
    organizationId: Number(result.organizationId ?? result.organization_id ?? 0),
    dismissedPairs: Number(result.dismissedPairs ?? result.dismissed_pairs ?? 0),
    alreadyDismissedPairs: Number(
      result.alreadyDismissedPairs ?? result.already_dismissed_pairs ?? 0,
    ),
  }
}

export const mergeMyPricesDuplicates = async (options: {
  organizationId: number
  keepItemId: number
  mergeItemIds: number[]
  name?: string | null
  brand?: string | null
  price?: number | null
  country?: string | null
  unit?: string | null
  externalSku?: string | null
}): Promise<MergeMyPricesDuplicatesResult> => {
  const body: Record<string, unknown> = {
    organization_id: options.organizationId,
    keep_item_id: options.keepItemId,
    merge_item_ids: options.mergeItemIds,
  }
  if (options.name !== undefined) body.name = options.name
  if (options.brand !== undefined) body.brand = options.brand
  if (options.price !== undefined) body.price = options.price
  if (options.country !== undefined) body.country = options.country
  if (options.unit !== undefined) body.unit = options.unit
  if (options.externalSku !== undefined) body.external_sku = options.externalSku

  const result = await apiRequest<Raw>(`${API_PREFIX}/duplicates/merge`, {
    method: 'POST',
    body,
  })
  const keptRaw = (result.kept_item as Raw | undefined) ?? (result.keptItem as Raw | undefined) ?? {}
  return {
    keptItem: normalizeItem(keptRaw),
    mergedCount: Number(result.merged_count ?? result.mergedCount ?? 0),
  }
}
